import type { CSSProperties, ReactNode } from 'react';
import { figures, CAP, type HeroState } from '@/lib/website-hero/timeline';
import { FRAME_H, FRAME_W } from '@/lib/website-hero/geometry';
import { ATHLETE, EVENT, ORG, THEME } from '@/lib/website-hero/est28';

/**
 * The organiser dashboard inside the hero frame, laid out at 1280 x 800 and
 * scaled by the parent. The redesign's shell: rail, top bar with search and the
 * bell, then either Home or the event's Overview.
 *
 * The organiser is est.28 and the event is their Hybrid Simulation, the same
 * one the athlete's phone opens (lib/website-hero/est28.ts). The people and
 * figures are invented.
 * Copy follows the product: "event day", never "race day", because not every
 * event on the platform is a race.
 */

const SANS = 'var(--font-geist-sans), "Instrument Sans", system-ui, sans-serif';
const MONO = 'var(--font-geist-mono), ui-monospace, monospace';
const WORDMARK = 'var(--font-schibsted-grotesk), system-ui, sans-serif';

const C = {
  ink: '#0C1512',
  body: '#4E5B57',
  mute: '#5A6864',
  label: '#6A7671',
  faint: '#93A09B',
  icon: '#7A8783',
  teal: '#006D6D',
  tealText: '#00615F',
  amber: '#C08A2E',
  amberText: '#9A6412',
  rule: 'rgba(12,21,18,.09)',
  ruleSoft: 'rgba(12,21,18,.07)',
  track: '#EDF0EF',
  tint: '#F1F5F4',
  selected: '#ECF1F0',
} as const;

const mono: CSSProperties = { fontFamily: MONO };
const nowrap: CSSProperties = { whiteSpace: 'nowrap' };

/* ── Icons (the rail's own set, stroked at 1.7) ── */

const ICONS = {
  home: <><path d="M4 10.5 12 4l8 6.5" /><path d="M6 10v9.5h12V10" /></>,
  calendar: <><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 10h17M8 3.5v3M16 3.5v3" /></>,
  tasks: <path d="M4.5 6.5h2M4.5 12h2M4.5 17.5h2M9.5 6.5h10M9.5 12h10M9.5 17.5h10" />,
  people: <><circle cx="9.5" cy="8.5" r="3.2" /><path d="M4 19a5.5 5.5 0 0 1 11 0" /><path d="M16 5.8a3 3 0 0 1 0 5.5M17.5 19a5.6 5.6 0 0 0-1.8-3.7" /></>,
  heart: <path d="M12 19.5S5.5 15.6 5.5 11a3.9 3.9 0 0 1 6.5-2.6A3.9 3.9 0 0 1 18.5 11c0 4.6-6.5 8.5-6.5 8.5Z" />,
  megaphone: <><path d="M3.5 10.5v3a1 1 0 0 0 1 1h2l8.5 4.5v-14L6.5 9.5h-2a1 1 0 0 0-1 1Z" /><path d="M18.5 9.5a3 3 0 0 1 0 5" /></>,
  card: <><rect x="3.5" y="6" width="17" height="12.5" rx="2.5" /><path d="M3.5 10.5h17" /><circle cx="16.5" cy="14.5" r="1.1" /></>,
  cog: <><circle cx="12" cy="12" r="3" /><path d="M12 3.5v2.2M12 18.3v2.2M20.5 12h-2.2M5.7 12H3.5M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6M18.4 18.4l-1.6-1.6M7.2 7.2 5.6 5.6" /></>,
  receipt: <><path d="M5 4.5h14v15l-3-2-2 2-2-2-2 2-2-2-3 2v-15Z" /><path d="M8.5 9h7M8.5 13h4" /></>,
  grid: <><rect x="4" y="4" width="7" height="7" rx="1.8" /><rect x="13" y="4" width="7" height="7" rx="1.8" /><rect x="4" y="13" width="7" height="7" rx="1.8" /><rect x="13" y="13" width="7" height="7" rx="1.8" /></>,
  chat: <><path d="M4.5 6.5h15v9a1 1 0 0 1-1 1H9l-4.5 3.5V6.5Z" /><path d="M9 11h6" /></>,
  palette: <><path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.2 0 1.8-.8 1.8-1.7 0-1.6 1-2 2.2-2h1.3a3.2 3.2 0 0 0 3.2-3.2C20.5 8 16.7 3.5 12 3.5Z" /><circle cx="8" cy="10" r="1" /><circle cx="12" cy="7.5" r="1" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.3V12l3 2" /></>,
  groups: <><circle cx="7.5" cy="9" r="2.6" /><circle cx="16.5" cy="9" r="2.6" /><path d="M3.5 18a4 4 0 0 1 8 0M12.5 18a4 4 0 0 1 8 0" /></>,
  ticket: <><path d="M4 9.5a2 2 0 0 0 0 5V18a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3.5a2 2 0 0 1 0-5V6a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v3.5Z" /><path d="M13 8v8" /></>,
  bag: <><path d="M6 8.5h12l-1 11H7Z" /><path d="M9.5 8.5V6.5a2.5 2.5 0 0 1 5 0v2" /></>,
  star: <path d="m12 4.5 2.3 4.8 5.2.7-3.8 3.6.9 5.2-4.6-2.5-4.6 2.5.9-5.2L4.5 10l5.2-.7Z" />,
  checkin: <><path d="M4.5 8V5.5a1 1 0 0 1 1-1H8M16 4.5h2.5a1 1 0 0 1 1 1V8M19.5 16v2.5a1 1 0 0 1-1 1H16M8 19.5H5.5a1 1 0 0 1-1-1V16" /><path d="m8.5 12 2.5 2.5 4.5-4.5" /></>,
  trophy: <><path d="M7.5 4.5h9v4a4.5 4.5 0 0 1-9 0v-4Z" /><path d="M12 13v3.5M8.5 19.5h7" /></>,
  chart: <path d="M4.5 19.5V14M9.5 19.5V8M14.5 19.5v-7M19.5 19.5V5" />,
  mail: <><rect x="3.5" y="5.5" width="17" height="13" rx="2.5" /><path d="m4 7 8 5.5L20 7" /></>,
  send: <path d="M20.5 3.5 3.5 10l7 3 3 7 7-16.5Z" />,
  download: <><path d="M12 4v10" /><path d="m8 10.5 4 4 4-4" /><path d="M4.5 18.5h15" /></>,
  addPerson: <><circle cx="9.5" cy="8.5" r="3.2" /><path d="M4 19a5.5 5.5 0 0 1 11 0" /><path d="M18.5 7v5M21 9.5h-5" /></>,
} as const;

function Icon({ name, size = 16, color = C.icon, width = 1.7 }: { name: keyof typeof ICONS; size?: number; color?: string; width?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', display: 'block' }}>
      {ICONS[name]}
    </svg>
  );
}

/* ── Rail ── */

function RailGroup({ children, first }: { children: ReactNode; first?: boolean }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: C.label, padding: first ? '12px 10px 5px' : '14px 10px 5px' }}>
      {children}
    </div>
  );
}

function RailItem({ icon, label, count, amber, active }: { icon: keyof typeof ICONS; label: string; count?: ReactNode; amber?: boolean; active?: boolean }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, height: 30, padding: '0 10px', borderRadius: 7,
        background: active ? C.selected : 'transparent', color: active ? C.ink : C.body,
        fontSize: 13, fontWeight: active ? 600 : 450,
      }}
    >
      <Icon name={icon} color={active ? C.teal : C.icon} />
      <span style={{ flex: 1 }}>{label}</span>
      {count !== undefined && <span style={{ ...mono, fontSize: 11, color: amber ? C.amberText : C.label }}>{count}</span>}
    </div>
  );
}

function OrgTile({ size = 22, radius = 6 }: { size?: number; radius?: number }) {
  // est.28's gold wordmark needs its own ground on a light rail. Their navy,
  // the page they chose it for.
  return (
    <span style={{ flex: 'none', width: size, height: size, borderRadius: radius, background: THEME.ground, color: THEME.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-barlow-condensed), sans-serif', fontSize: size * 0.5, fontWeight: 700, letterSpacing: '-0.02em' }}>
      28
    </span>
  );
}

function Rail({ state }: { state: HeroState }) {
  const onEvent = state.view === 'event';
  const f = figures(state.entries);
  return (
    <aside style={{ position: 'relative', background: '#FCFCFC', borderRight: `1px solid ${C.rule}`, padding: '15px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '0 2px' }}>
        <svg width="16" height="16" viewBox="0 0 100 100" aria-hidden style={{ flex: 'none', display: 'block' }}>
          <polygon points="8,14 92,14 92,28 22,28" fill="#006D6D" />
          <polygon points="34,44 92,44 92,58 48,58" fill="#10201D" />
          <polygon points="68,74 92,74 92,88 78,88" fill="#10201D" />
        </svg>
        <span style={{ flex: 1, fontFamily: WORDMARK, fontSize: 15, fontWeight: 700, letterSpacing: '-.043em', color: '#10201D' }}>fitvent</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.icon} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="4.5" width="17" height="15" rx="2.5" /><path d="M9.5 4.5v15" /></svg>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 13, padding: '7px 8px', border: '1px solid rgba(12,21,18,.1)', borderRadius: 8, background: '#FFFFFF' }}>
        <OrgTile />
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 500, ...nowrap }}>{ORG.name}</span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6A7671" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M8 9.5 12 5.5l4 4" /><path d="M16 14.5 12 18.5l-4-4" /></svg>
      </div>

      <div style={{ position: 'relative' }}>
        {/* Organiser level */}
        <div style={{ opacity: onEvent ? 0 : 1, transition: 'opacity .35s ease' }}>
          <RailGroup first>Organiser</RailGroup>
          <RailItem icon="home" label="Home" active />
          <RailItem icon="calendar" label="Events" count={2} />
          <RailItem icon="people" label="Registrants" count={38 + f.entries} />
          <RailItem icon="heart" label="Volunteers" count={14} />
          <RailItem icon="megaphone" label="Campaigns" />
          <RailItem icon="card" label="Finance" />
          <RailGroup>Workspace</RailGroup>
          <RailItem icon="cog" label="Settings" />
          <RailItem icon="receipt" label="Billing" />
        </div>

        {/* Event level */}
        <div style={{ position: 'absolute', left: 0, right: 0, top: 0, opacity: onEvent ? 1 : 0, transition: 'opacity .35s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 11, padding: '7px 9px', border: '1px solid rgba(12,21,18,.11)', borderRadius: 8, background: '#FFFFFF' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.body} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5.5" /><path d="m11 5.5-6 6.5 6 6.5" /></svg>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 10, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: C.label }}>All events</span>
              <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginTop: 1, ...nowrap, overflow: 'hidden', textOverflow: 'ellipsis' }}>{EVENT.name}</span>
            </span>
          </div>
          <RailGroup>Event</RailGroup>
          <RailItem icon="grid" label="Overview" active />
          <RailItem icon="chat" label="Requests" count={3} amber />
          <RailItem icon="cog" label="Setup" />
          <RailItem icon="palette" label="Branding" />
          <RailItem icon="clock" label="Schedule" />
          <RailGroup>People</RailGroup>
          <RailItem icon="people" label="Registrants" count={f.entries} />
          <RailItem icon="heart" label="Volunteers" count={9} />
          <RailItem icon="groups" label="Groups" count={11} />
          <RailGroup>Manage</RailGroup>
          <RailItem icon="ticket" label="Tickets" />
          <RailItem icon="bag" label="Ancillaries" count={6} />
          <RailItem icon="star" label="Sponsors" count={4} />
          <RailItem icon="checkin" label="Check-in" />
          <RailItem icon="trophy" label="Scoring" />
          <RailItem icon="chart" label="Analytics" />
          <RailItem icon="mail" label="Comms" count={4} />
        </div>
      </div>

      <div style={{ position: 'absolute', left: 14, right: 14, bottom: 12, display: 'flex', alignItems: 'center', gap: 9, padding: '9px 8px 0', borderTop: '1px solid rgba(12,21,18,.08)', background: '#FCFCFC' }}>
        <span style={{ flex: 'none', width: 25, height: 25, borderRadius: '50%', background: C.teal, color: '#F2F6F5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>SK</span>
        <span style={{ flex: 1, fontSize: 12.5 }}>Sam Keane</span>
      </div>
    </aside>
  );
}

/* ── Top bar: search, help, the bell, and the two popovers under it ── */

function SearchResult({ before = '', title, rest, sub, icon, hot, pressed, first }: { before?: string; title: string; rest: string; sub: string; icon: keyof typeof ICONS; hot?: boolean; pressed?: boolean; first?: boolean }) {
  return (
    <div data-hero-target={first ? 'searchResult' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 11, height: 48, padding: '0 10px', borderRadius: 8, background: pressed ? '#E3F1EE' : hot ? C.tint : 'transparent', transition: 'background .15s ease' }}>
      <span style={{ flex: 'none', width: 28, height: 28, borderRadius: 7, background: C.tint, color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={icon} size={15} color="currentColor" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, ...nowrap }}>{before}<strong style={{ fontWeight: 600 }}>{title}</strong>{rest}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: C.mute, marginTop: 2, ...nowrap }}>{sub}</span>
      </span>
      {first && (
        <span style={{ flex: 'none', height: 20, padding: '0 6px', border: '1px solid rgba(12,21,18,.14)', borderRadius: 5, ...mono, fontSize: 10.5, color: C.body, display: 'flex', alignItems: 'center', opacity: hot ? 1 : 0, transition: 'opacity .15s ease' }}>↵</span>
      )}
    </div>
  );
}

function TopBar({ state }: { state: HeroState }) {
  const typed = state.text;
  return (
    <div style={{ position: 'relative', zIndex: 4, flex: 'none', display: 'flex', alignItems: 'center', gap: 14, height: 52, padding: '0 24px', borderBottom: `1px solid ${C.rule}` }}>
      <div
        style={{
          flex: 'none', width: 420, display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 11px', borderRadius: 8,
          border: `1px solid ${state.focus ? C.teal : 'rgba(12,21,18,.12)'}`,
          boxShadow: state.focus ? '0 0 0 3px rgba(0,109,109,.12)' : 'none',
          transition: 'border-color .2s ease, box-shadow .2s ease',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6A7671" strokeWidth="1.9" strokeLinecap="round"><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /></svg>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', fontSize: 12.5, ...nowrap, overflow: 'hidden' }}>
          <span style={{ color: C.ink }}>{typed}</span>
          <span className="fvh-caret" style={{ width: 1.5, height: 15, marginLeft: 1, background: C.teal, opacity: state.focus ? 1 : 0 }} />
          <span style={{ color: C.faint, opacity: typed || state.focus ? 0 : 1 }}>Search events, athletes, payouts</span>
        </span>
        <span style={{ ...mono, fontSize: 10.5, color: '#9FAAA5' }}>⌘K</span>
      </div>
      <span style={{ flex: 1 }} />
      <span style={{ display: 'flex', color: C.body }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="8.5" /><path d="M9.7 9.6a2.4 2.4 0 1 1 2.9 2.6v1.2" /><path d="M12.5 16.5h.01" /></svg>
      </span>
      <span style={{ position: 'relative', display: 'flex', color: C.body }}>
        <span style={{ display: 'flex', transformOrigin: '50% 3px', transform: `rotate(${state.bell}deg)`, transition: 'transform .09s ease' }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 10a5.5 5.5 0 0 1 11 0c0 4 1.5 5.5 1.5 5.5H5S6.5 14 6.5 10Z" /><path d="M10.2 18.5a2 2 0 0 0 3.6 0" /></svg>
        </span>
        <span
          style={{
            position: 'absolute', top: -5, right: -7, minWidth: 15, height: 15, padding: '0 4px', boxSizing: 'border-box', borderRadius: 99,
            background: C.amber, color: '#FFFFFF', ...mono, fontSize: 9.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 0 2px #FFFFFF',
            opacity: state.badge ? 1 : 0, transform: `scale(${state.badge ? 1 : 0.4})`,
            transition: 'opacity .2s ease, transform .3s cubic-bezier(.3,1.6,.5,1)',
          }}
        >
          1
        </span>
      </span>

      {/* The new entry, under the bell */}
      <div
        style={{
          position: 'absolute', top: 46, right: 14, width: 320, padding: '12px 14px', border: '1px solid rgba(12,21,18,.1)', borderRadius: 12, background: '#FFFFFF',
          boxShadow: '0 16px 38px rgba(12,21,18,.16), 0 2px 6px rgba(12,21,18,.06)', display: 'flex', alignItems: 'center', gap: 11, pointerEvents: 'none',
          opacity: state.note ? 1 : 0, transform: `translateY(${state.note ? 0 : -6}px)`,
          transition: 'opacity .3s ease, transform .45s cubic-bezier(.2,.7,.2,1)',
        }}
      >
        <span style={{ flex: 'none', width: 32, height: 32, borderRadius: '50%', background: C.teal, color: '#F2F6F5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>MK</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, color: C.tealText }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.teal }} />New entry<span style={{ fontWeight: 400, color: C.mute }}>just now</span>
          </span>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 500, marginTop: 3, ...nowrap }}>{ATHLETE.name} &amp; {ATHLETE.partner}</span>
          <span style={{ display: 'block', fontSize: 12, color: C.mute, marginTop: 1 }}>{ATHLETE.division} · <span style={mono}>£150</span></span>
        </span>
      </div>

      {/* Search results */}
      <div
        style={{
          position: 'absolute', top: 46, left: 24, width: 420, padding: 6, border: '1px solid rgba(12,21,18,.1)', borderRadius: 12, background: '#FFFFFF',
          boxShadow: '0 16px 38px rgba(12,21,18,.16), 0 2px 6px rgba(12,21,18,.06)', pointerEvents: 'none',
          opacity: state.results ? 1 : 0, transform: `translateY(${state.results ? 0 : -4}px)`,
          transition: 'opacity .2s ease, transform .3s cubic-bezier(.2,.7,.2,1)',
        }}
      >
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: C.label, padding: '6px 10px 4px' }}>Events</div>
        <SearchResult first icon="calendar" before="EST.28 " title="Hybrid" rest=" Simulation 2027" sub={`${EVENT.dateShort} · On sale · 64 entered`} hot={state.resultHot} pressed={state.resultPressed} />
        <SearchResult icon="calendar" before="EST.28 " title="Hybrid" rest=" Simulation 2026" sub="Past event · 69 raced" />
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: C.label, padding: '8px 10px 4px' }}>Payouts</div>
        <SearchResult icon="card" title="Hybrid" rest=" Simulation 2026 payout" sub="£4,210 · paid to your bank" />
      </div>
    </div>
  );
}

/* ── Shared bits for the two views ── */

function SectionTitle({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-.01em', ...style }}>{children}</div>;
}

function Button({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span style={{ flex: 'none', height: 30, padding: '0 12px', border: '1px solid rgba(12,21,18,.13)', borderRadius: 7, fontSize: 12.5, fontWeight: 500, display: 'flex', alignItems: 'center', ...nowrap, ...style }}>
      {children}
    </span>
  );
}

function Dismiss() {
  return (
    <span style={{ flex: 'none', width: 22, display: 'flex', justifyContent: 'center', color: C.faint }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /></svg>
    </span>
  );
}

function Bars({ heights, colours }: { heights: number[]; colours: string[] }) {
  return (
    <>
      {heights.map((h, i) => (
        <div key={i} style={{ flex: 1, height: `${h}%`, background: colours[i], borderRadius: '3px 3px 0 0' }} />
      ))}
    </>
  );
}

/* ── Home ── */

const TASKS: Array<{ title: string; sub: string; due: string; action: string; urgent: boolean }> = [
  { title: 'Schedule start waves', sub: "Hybrid Simulation, athletes can't be told when they're racing", due: 'by 23 Aug', action: 'Schedule', urgent: true },
  { title: 'Set a capacity cap', sub: 'Kids and Teens Simulation, you could sell past what the gym holds', due: 'no date', action: 'Set cap', urgent: true },
  { title: 'Conor and Niall want to switch division', sub: "Hybrid Simulation, Men's Pairs to Mixed", due: '1h ago', action: 'Review', urgent: false },
];

function OnSaleRow({ day, month, name, sub, entered, of, pct, pace, paceTone, taken }: { day: string; month: string; name: string; sub: string; entered: number; of: number; pct: number; pace: string; paceTone: string; taken: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, height: 66, borderBottom: `1px solid ${C.ruleSoft}` }}>
      <span style={{ flex: 'none', width: 44, textAlign: 'center' }}>
        <span style={{ display: 'block', ...mono, fontSize: 16, letterSpacing: '-.03em', lineHeight: 1 }}>{day}</span>
        <span style={{ display: 'block', fontSize: 10.5, letterSpacing: '.09em', textTransform: 'uppercase', color: C.mute, marginTop: 3 }}>{month}</span>
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 500, letterSpacing: '-.014em', ...nowrap }}>{name}</span>
        <span style={{ display: 'block', fontSize: 12.5, color: C.mute, marginTop: 3, ...nowrap }}>{sub}</span>
      </span>
      <span style={{ flex: 'none', width: 58, textAlign: 'right' }}>
        <span style={{ display: 'block', ...mono, fontSize: 13.5 }}>{entered}</span>
        <span style={{ display: 'block', fontSize: 11, color: C.mute, marginTop: 3 }}>of {of}</span>
      </span>
      <span style={{ flex: 'none', width: 92 }}>
        <span style={{ display: 'block', height: 3, borderRadius: 99, background: C.track }}>
          <span style={{ display: 'block', width: `${pct}%`, height: '100%', borderRadius: 99, background: C.teal }} />
        </span>
        <span style={{ display: 'block', fontSize: 11, color: paceTone, marginTop: 6 }}>{pace}</span>
      </span>
      <span style={{ flex: 'none', width: 72, textAlign: 'right', ...mono, fontSize: 13.5 }}>{taken}</span>
    </div>
  );
}

function Segmented({ items, active }: { items: string[]; active: string }) {
  return (
    <div style={{ display: 'flex', gap: 2, padding: 2, background: '#F1F4F3', borderRadius: 8 }}>
      {items.map((it) => (
        <span key={it} style={{ height: 24, padding: '0 10px', borderRadius: 6, background: it === active ? '#FFFFFF' : 'transparent', fontSize: 12, fontWeight: it === active ? 600 : 400, color: it === active ? C.ink : C.body, display: 'flex', alignItems: 'center' }}>
          {it}
        </span>
      ))}
    </div>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '11px 0', borderBottom: `1px solid ${C.ruleSoft}` }}>
      <span style={{ fontSize: 13, color: C.body }}>{label}</span>
      <span style={{ ...mono, fontSize: 13.5 }}>{value}</span>
    </div>
  );
}

function Quick({ icon, label }: { icon: keyof typeof ICONS; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 0', borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 13 }}>
      <Icon name={icon} color={C.mute} />
      {label}
    </div>
  );
}

function HomeView({ state }: { state: HeroState }) {
  const f = figures(state.entries);
  return (
    <div style={{ position: 'absolute', inset: 0, padding: '30px 40px', opacity: state.view === 'home' ? 1 : 0, transition: 'opacity .35s ease' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-.028em' }}>Morning, Sam</div>
          <div style={{ fontSize: 13.5, color: C.body, marginTop: 8 }}>The Hybrid Simulation is 21 days out and entries close in 5. Three things need you today.</div>
        </div>
        <span style={{ flex: 'none', height: 34, padding: '0 14px', borderRadius: 8, background: C.teal, color: '#F2F6F5', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 7 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5.5v13M5.5 12h13" /></svg>
          Create an event
        </span>
      </div>

      <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${C.rule}` }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
          <SectionTitle>Needs you today</SectionTitle>
          <span style={{ ...mono, fontSize: 13, color: C.amberText }}>7</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: C.mute }}>Across 2 live events</span>
        </div>
        <div style={{ marginTop: 4 }}>
          {TASKS.map((t) => (
            <div key={t.title} style={{ display: 'flex', alignItems: 'center', gap: 13, height: 60, borderBottom: `1px solid ${C.ruleSoft}` }}>
              <span style={{ flex: 'none', width: 3, height: 30, borderRadius: 2, background: t.urgent ? C.amber : '#CBD3D0' }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 500, letterSpacing: '-.01em', ...nowrap }}>{t.title}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: C.mute, marginTop: 2, ...nowrap }}>{t.sub}</span>
              </span>
              <span style={{ flex: 'none', fontSize: 12, color: t.urgent ? C.amberText : C.mute, ...nowrap }}>{t.due}</span>
              <Button>{t.action}</Button>
              <Dismiss />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, fontSize: 12.5, fontWeight: 500, color: C.tealText }}>
          {/* As the product's Home: "Show N more", no task centre (there is none). */}
          <span>Show 4 more</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 40, marginTop: 28 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <SectionTitle>On sale now</SectionTitle>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12.5, fontWeight: 500, color: C.tealText }}>All events</span>
          </div>
          <OnSaleRow day="11" month="Sep" name={EVENT.name} sub="Entries close in 5 days · 3 things to sort" entered={f.entries} of={CAP} pct={f.soldPct} pace="On pace" paceTone={C.tealText} taken={f.takenLabel} />
          <OnSaleRow day="25" month="Sep" name="Kids and Teens Simulation" sub="Nothing needs you" entered={38} of={60} pct={63} pace="Ahead of pace" paceTone={C.mute} taken="£1,140" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 24 }}>
            <Segmented items={['Revenue', 'Entries']} active="Revenue" />
            <Segmented items={['Month', '3 months', 'Year']} active="3 months" />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 14 }}>
            <span style={{ ...mono, fontSize: 26, letterSpacing: '-.03em' }}>£9,420</span>
            <span style={{ fontSize: 12.5, color: C.tealText }}>up 31% on the previous 3</span>
          </div>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: 5, height: 96, marginTop: 14, paddingRight: 42, borderBottom: '1px solid rgba(12,21,18,.14)' }}>
            <Bars
              heights={[34, 41, 38, 52, 47, 61, 56, 68, 64, 79, 73, 88, 100]}
              colours={['#D3E2DF', '#D3E2DF', '#D3E2DF', '#D3E2DF', '#D3E2DF', '#D3E2DF', '#D3E2DF', '#BCD8D4', '#BCD8D4', '#BCD8D4', '#8CC4BE', '#8CC4BE', C.teal]}
            />
            <span style={{ position: 'absolute', right: 0, top: -6, ...mono, fontSize: 10.5, color: C.label }}>£1.4k</span>
            <span style={{ position: 'absolute', right: 0, top: 42, ...mono, fontSize: 10.5, color: C.label }}>£700</span>
          </div>
        </div>
        <div style={{ minWidth: 0 }}>
          <SectionTitle style={{ marginBottom: 4 }}>Your money</SectionTitle>
          <KeyValue label="Paying out Friday" value="£960" />
          <KeyValue label="Still clearing" value="£610" />
          <KeyValue label="Taken this season" value="£14,280" />
          <div style={{ fontSize: 12.5, fontWeight: 500, color: C.tealText, marginTop: 12 }}>See all payouts</div>
          <SectionTitle style={{ margin: '26px 0 4px', paddingTop: 22, borderTop: `1px solid ${C.rule}` }}>Quick things</SectionTitle>
          <Quick icon="send" label="Email everyone entered" />
          <Quick icon="download" label="Download your athlete list" />
          <Quick icon="addPerson" label="Invite a volunteer" />
        </div>
      </div>
    </div>
  );
}

/* ── Event overview ── */

function Division({ name, count, cap, pct, tone = C.teal, note }: { name: string; count: number; cap: number; pct: number; tone?: string; note?: string }) {
  return (
    <div style={{ padding: '10px 0', borderBottom: `1px solid ${C.ruleSoft}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ flex: 1, fontSize: 13 }}>{name}</span>
        <span style={{ ...mono, fontSize: 12.5 }}>{count}<span style={{ color: C.mute }}> / {cap}</span></span>
      </div>
      <div style={{ height: 3, marginTop: 7, borderRadius: 99, background: C.track, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: tone, borderRadius: 99, transition: 'width .6s ease' }} />
      </div>
      {note && <div style={{ fontSize: 11.5, color: C.amberText, marginTop: 5 }}>{note}</div>}
    </div>
  );
}

function Activity({ initials, who, what, when, fresh }: { initials: string; who: string; what: string; when: string; fresh?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 46, borderBottom: `1px solid ${C.ruleSoft}` }}>
      <span style={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', background: fresh ? C.teal : C.tint, color: fresh ? '#F2F6F5' : C.body, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>{initials}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.4, ...nowrap, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <strong style={{ fontWeight: 600 }}>{who}</strong> {what}
      </span>
      <span style={{ flex: 'none', fontSize: 11.5, color: C.mute }}>{when}</span>
    </div>
  );
}

function EventView({ state }: { state: HeroState }) {
  const f = figures(state.entries);
  const on = state.view === 'event';
  const vp = state.viewPublicPressed;
  return (
    <div style={{ position: 'absolute', inset: 0, padding: '30px 40px', opacity: on ? 1 : 0, transform: `translateY(${on ? 0 : 8}px)`, transition: 'opacity .4s ease, transform .5s cubic-bezier(.2,.7,.2,1)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-.028em', ...nowrap }}>{EVENT.name}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 22, padding: '0 8px', borderRadius: 6, background: '#EAF6F4', color: C.tealText, fontSize: 11.5, fontWeight: 500 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.teal }} />On sale
            </span>
          </div>
          <div style={{ fontSize: 13.5, color: C.body, marginTop: 7 }}>Sat 11 September · est.28, Crabtree Hill, {EVENT.venueShort}</div>
        </div>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 11px', border: '1px solid rgba(12,21,18,.12)', borderRadius: 8, fontSize: 12.5 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.body} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3.5v6.2L4.9 18a1.6 1.6 0 0 0 1.4 2.5h11.4a1.6 1.6 0 0 0 1.4-2.5L14 9.7V3.5" /><path d="M8.6 3.5h6.8" /></svg>
            Test mode
            <span style={{ width: 30, height: 18, borderRadius: 99, background: '#D5DBD9', display: 'flex', alignItems: 'center', padding: 2, boxSizing: 'border-box' }}>
              <span style={{ width: 14, height: 14, borderRadius: '50%', background: '#FFFFFF' }} />
            </span>
          </span>
          <span style={{ height: 32, padding: '0 12px', border: '1px solid rgba(12,21,18,.12)', borderRadius: 8, fontSize: 12.5, display: 'flex', alignItems: 'center', ...nowrap }}>Copy link</span>
          <span
            data-hero-target="viewPublic"
            style={{
              height: 32, padding: '0 12px', borderRadius: 8, fontSize: 12.5, display: 'flex', alignItems: 'center', ...nowrap,
              border: `1px solid ${vp ? C.teal : 'rgba(12,21,18,.12)'}`, background: vp ? '#EAF6F4' : '#FFFFFF', color: vp ? C.tealText : C.ink,
              transform: `scale(${vp ? 0.95 : 1})`,
              transition: 'transform .12s ease, background .2s ease, border-color .2s ease, color .2s ease',
            }}
          >
            View public page
          </span>
        </div>
      </div>

      {/* Countdown. The product's track carries entries closing and event day;
          waves are a task with a due date, so they sit in the list below. */}
      <div style={{ marginTop: 24, paddingBottom: 22, borderBottom: `1px solid ${C.rule}` }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ ...mono, fontSize: 13, fontWeight: 500 }}>21 days</span>
          <span style={{ fontSize: 13, color: C.body }}>until event day, {EVENT.dateShort}</span>
        </div>
        <div style={{ position: 'relative', height: 3, marginTop: 16, borderRadius: 2, background: C.track }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 2, background: C.teal, width: state.track ? '64%' : '0%', transition: 'width 1.1s cubic-bezier(.4,0,.2,1)' }} />
          <div style={{ position: 'absolute', top: -4, width: 11, height: 11, marginLeft: -5, borderRadius: '50%', background: C.teal, border: '2.5px solid #FFFFFF', boxShadow: `0 0 0 1px ${C.teal}`, boxSizing: 'border-box', left: state.track ? '64%' : '0%', transition: 'left 1.1s cubic-bezier(.4,0,.2,1)' }} />
          <div style={{ position: 'absolute', left: '79%', top: -2, width: 1, height: 7, background: '#C6CECB' }} />
        </div>
        <div style={{ display: 'flex', gap: 26, marginTop: 14, fontSize: 12.5 }}>
          <span>Entries close <span style={{ ...mono, fontSize: 12, color: C.amberText }}>in 5 days</span> <span style={{ color: C.mute }}>26 Aug</span></span>
          <span>Event day <span style={{ ...mono, fontSize: 12, color: C.mute }}>in 21 days</span> <span style={{ color: C.mute }}>{EVENT.dayMonth}</span></span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 40, marginTop: 24 }}>
        <div style={{ minWidth: 0 }}>
          <SectionTitle>Entries</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginTop: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ ...mono, fontSize: 34, letterSpacing: '-.035em', lineHeight: 1 }}>{f.entries}</span>
                <span style={{ fontSize: 13, color: C.mute }}>of {CAP} · {f.soldPct}% sold</span>
              </div>
              <div style={{ fontSize: 12.5, color: C.tealText, marginTop: 8 }}>On pace to sell out before entries close</div>
            </div>
            <div style={{ flex: 'none', textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: C.mute }}>Taken so far</div>
              <div style={{ ...mono, fontSize: 15, marginTop: 4 }}>{f.takenLabel}</div>
            </div>
          </div>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: 6, height: 92, marginTop: 16, borderBottom: '1px solid rgba(12,21,18,.14)' }}>
            <div style={{ position: 'absolute', left: 0, right: 0, top: 0, borderTop: `1px dashed ${C.amber}` }} />
            <span style={{ position: 'absolute', right: 0, top: -17, fontSize: 10.5, color: C.amberText }}>Cap {CAP}</span>
            <Bars heights={[12, 22, 31, 42, 51, 60, 68, 75]} colours={['#BCD8D4', '#BCD8D4', '#BCD8D4', '#BCD8D4', '#BCD8D4', '#BCD8D4', '#BCD8D4', C.teal]} />
            {[84, 92].map((h) => (
              <div key={h} style={{ flex: 1, height: `${h}%`, borderRadius: '3px 3px 0 0', border: '1px dashed #8CC4BE', borderBottom: 'none', boxSizing: 'border-box', background: 'repeating-linear-gradient(135deg, rgba(140,196,190,.35) 0 4px, transparent 4px 8px)' }} />
            ))}
          </div>
          <SectionTitle style={{ marginTop: 24 }}>Before event day</SectionTitle>
          <div style={{ marginTop: 4 }}>
            {[
              { title: 'Schedule start waves', sub: "Athletes can't be told when they're racing", due: 'by 23 Aug', action: 'Schedule' },
              { title: 'Book your volunteers', sub: '9 of 12 roles filled', due: 'by 4 Sep', action: 'Invite' },
            ].map((t) => (
              <div key={t.title} style={{ display: 'flex', alignItems: 'center', gap: 12, height: 56, borderBottom: `1px solid ${C.ruleSoft}` }}>
                <span style={{ flex: 'none', width: 18, height: 18, borderRadius: '50%', border: `1.5px solid ${C.amber}`, boxSizing: 'border-box' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 500, ...nowrap }}>{t.title}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: C.mute, marginTop: 2, ...nowrap }}>{t.sub}</span>
                </span>
                <span style={{ flex: 'none', ...mono, fontSize: 11.5, color: C.amberText }}>{t.due}</span>
                <Button style={{ height: 28, padding: '0 11px' }}>{t.action}</Button>
              </div>
            ))}
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <SectionTitle style={{ marginBottom: 2 }}>Divisions</SectionTitle>
          <Division name="Men's Singles" count={22} cap={30} pct={73} />
          <Division name="Women's Singles" count={10} cap={20} pct={50} tone={C.amber} note="Slowest, 10 places left" />
          <Division name={ATHLETE.division} count={f.division} cap={20} pct={f.divisionPct} />
          <Division name="Mixed Pairs" count={18} cap={20} pct={90} />
          <SectionTitle style={{ margin: '22px 0 2px' }}>Latest activity</SectionTitle>
          <div style={{ overflow: 'hidden', maxHeight: state.row ? 46 : 0, opacity: state.row ? 1 : 0, background: state.row ? '#F4FAF9' : '#FFFFFF', transition: 'max-height .5s cubic-bezier(.4,0,.2,1), opacity .4s ease, background 1.4s ease' }}>
            <Activity fresh initials={ATHLETE.initials} who="Maria & Aoife" what={`entered ${ATHLETE.division}`} when="now" />
          </div>
          <Activity initials="CL" who="Conor Lynch" what="entered Men's Singles" when="12m" />
          <Activity initials="OC" who="Oakfield Coffee" what="paid their sponsorship" when="1h" />
        </div>
      </div>
    </div>
  );
}

/** The whole 1280 x 800 dashboard. */
export function HeroDashboard({ state }: { state: HeroState }) {
  return (
    <div
      style={{
        width: FRAME_W, height: FRAME_H, display: 'grid', gridTemplateColumns: '238px minmax(0,1fr)',
        fontFamily: SANS, color: C.ink, textAlign: 'left', background: '#FFFFFF', fontVariantNumeric: 'tabular-nums',
      }}
    >
      <Rail state={state} />
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', background: '#FFFFFF' }}>
        <TopBar state={state} />
        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <HomeView state={state} />
          <EventView state={state} />
        </div>
      </div>
    </div>
  );
}
